import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Filters, Plus } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { useBreakpointsContext } from "@air/provider-media-query";
import classNames from "classnames";
import { memo, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { CreatedDateFilterCard } from "~/components/Filters/DateFilter/CreatedDateFilterCard";
import { ModifiedDateFilterCard } from "~/components/Filters/DateFilter/ModifiedDateFilterCard";
import { TakenDateFilterCard } from "~/components/Filters/DateFilter/TakenDateFilterCard";
import { UploadedDateFilterCard } from "~/components/Filters/DateFilter/UploadedDateFilterCard";
import { FiltersDropdownHeader } from "~/components/Filters/FiltersDropdown/FiltersDropdownHeader";
import { useFiltersDropdownController } from "~/components/Filters/FiltersDropdown/useFiltersDropdownController";
import { OtherFiltersCard } from "~/components/Filters/OtherFilters/OtherFiltersCard";
import { TypeFilterCard } from "~/components/Filters/TypeFilter/TypeFilterCard";
import { FiltersMenu } from "~/components/FiltersMenu/FiltersMenu";
import { useFiltersMenuOptions } from "~/components/FiltersMenu/useFiltersMenuOptions";
import { ADD_FILTER_BUTTON, APPLY_FILTERS_BUTTON, CLEAR_ALL_FILTERS_BUTTON } from "~/constants/testIDs";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
import { addFilterAction } from "~/store/filters/actions";
export const FiltersDropdown = /*#__PURE__*/ memo((param)=>{
    let { title, customFields = [], onApplyClick, disabledFilters = [], ColorFilterCard, TagsFilterCard, SmartTagsFilterCard, CameraFilterCard, SourceFilterCard, ExtensionFilterCard, UploaderFilterCard, CustomFieldFilterCard, CopyrightFilterCard, CreatorFilterCard, availableTypes = [
        "boards",
        "assets",
        "files"
    ], availableFilters, otherFilters, secondaryCTA, trackLocation, isFilterChanged, ImportedKeywordsFilterCard, CountryFilterCard, CityFilterCard, StateFilterCard, OrientationFilterCard, VideoFrameRateFilterCard, VideoAspectRatioFilterCard, AudioCodingFilterCard, AudioSampleRateFilterCard, PeopleFilterCard, LocationsFilterCard } = param;
    const dispatch = useDispatch();
    const { searchImprovements } = useTemporaryFeatureFlags();
    const filtersContainerRef = useRef(null);
    const { getFiltersMenuOptions } = useFiltersMenuOptions();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { applySelectedFilters, clearSelectedFilters, selectedFilters } = useFiltersDropdownController({
        availableFilters,
        doAfterApply: onApplyClick,
        customFields,
        trackLocation
    });
    const onFilterSelect = useCallback(async (type, customField)=>{
        var _filtersContainerRef_current, _filtersContainerRef_current1;
        dispatch(addFilterAction({
            filter: {
                type,
                customField
            }
        }));
        (_filtersContainerRef_current1 = filtersContainerRef.current) === null || _filtersContainerRef_current1 === void 0 ? void 0 : _filtersContainerRef_current1.scrollTo({
            top: (_filtersContainerRef_current = filtersContainerRef.current) === null || _filtersContainerRef_current === void 0 ? void 0 : _filtersContainerRef_current.scrollHeight,
            behavior: "smooth"
        });
    }, [
        dispatch
    ]);
    const getFiltersOptions = useCallback((onSelect)=>getFiltersMenuOptions({
            availableFilters,
            selectedFilters,
            disabledFilters,
            onSelect,
            customFields
        }), [
        availableFilters,
        customFields,
        disabledFilters,
        getFiltersMenuOptions,
        selectedFilters
    ]);
    const addButtonFilters = useMemo(()=>getFiltersOptions(onFilterSelect), [
        getFiltersOptions,
        onFilterSelect
    ]);
    const cardProps = useMemo(()=>({
            getFiltersOptions,
            trackLocation
        }), [
        getFiltersOptions,
        trackLocation
    ]);
    const renderFilterCard = useCallback((filter)=>{
        switch(filter.type){
            case "assetFileKind":
            case "type":
                return /*#__PURE__*/ _jsx(TypeFilterCard, {
                    availableTypes: availableTypes,
                    ...cardProps
                }, filter.type);
            case "color":
                return ColorFilterCard ? /*#__PURE__*/ _jsx(ColorFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "tags":
                return TagsFilterCard ? /*#__PURE__*/ _jsx(TagsFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "smartTags":
                return SmartTagsFilterCard ? /*#__PURE__*/ _jsx(SmartTagsFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "other":
                return /*#__PURE__*/ _jsx(OtherFiltersCard, {
                    otherFilters: otherFilters,
                    ...cardProps
                }, filter.type);
            case "dateCreated":
                return /*#__PURE__*/ _jsx(CreatedDateFilterCard, {
                    ...cardProps
                }, filter.type);
            case "dateModified":
                return /*#__PURE__*/ _jsx(ModifiedDateFilterCard, {
                    ...cardProps
                }, filter.type);
            case "dateUploaded":
                return /*#__PURE__*/ _jsx(UploadedDateFilterCard, {
                    ...cardProps
                }, filter.type);
            case "dateTaken":
                return /*#__PURE__*/ _jsx(TakenDateFilterCard, {
                    ...cardProps
                }, filter.type);
            case "source":
                return SourceFilterCard ? /*#__PURE__*/ _jsx(SourceFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "extension":
                return ExtensionFilterCard ? /*#__PURE__*/ _jsx(ExtensionFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "creator":
                return CreatorFilterCard ? /*#__PURE__*/ _jsx(CreatorFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "uploader":
                return UploaderFilterCard ? /*#__PURE__*/ _jsx(UploaderFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "copyright":
                return CopyrightFilterCard ? /*#__PURE__*/ _jsx(CopyrightFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "importedKeywords":
                return ImportedKeywordsFilterCard ? /*#__PURE__*/ _jsx(ImportedKeywordsFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "camera":
                return CameraFilterCard ? /*#__PURE__*/ _jsx(CameraFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "customField":
                return filter.customField && CustomFieldFilterCard ? /*#__PURE__*/ _jsx(CustomFieldFilterCard, {
                    customField: filter.customField,
                    ...cardProps
                }, filter.customField.id) : null;
            case "country":
                return CountryFilterCard ? /*#__PURE__*/ _jsx(CountryFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "city":
                return CityFilterCard ? /*#__PURE__*/ _jsx(CityFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "state":
                return StateFilterCard ? /*#__PURE__*/ _jsx(StateFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "orientation":
                return OrientationFilterCard ? /*#__PURE__*/ _jsx(OrientationFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "videoFrameRate":
                return VideoFrameRateFilterCard ? /*#__PURE__*/ _jsx(VideoFrameRateFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "videoAspectRatio":
                return VideoAspectRatioFilterCard ? /*#__PURE__*/ _jsx(VideoAspectRatioFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "audioCoding":
                return AudioCodingFilterCard ? /*#__PURE__*/ _jsx(AudioCodingFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "audioSampleRate":
                return AudioSampleRateFilterCard ? /*#__PURE__*/ _jsx(AudioSampleRateFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "people":
                return PeopleFilterCard ? /*#__PURE__*/ _jsx(PeopleFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            case "locations":
                return LocationsFilterCard ? /*#__PURE__*/ _jsx(LocationsFilterCard, {
                    ...cardProps
                }, filter.type) : null;
            default:
                return "TBD";
        }
    }, [
        availableTypes,
        cardProps,
        ColorFilterCard,
        TagsFilterCard,
        SmartTagsFilterCard,
        otherFilters,
        SourceFilterCard,
        ExtensionFilterCard,
        CreatorFilterCard,
        UploaderFilterCard,
        CopyrightFilterCard,
        ImportedKeywordsFilterCard,
        CameraFilterCard,
        CustomFieldFilterCard,
        CountryFilterCard,
        CityFilterCard,
        StateFilterCard,
        OrientationFilterCard,
        VideoFrameRateFilterCard,
        VideoAspectRatioFilterCard,
        AudioCodingFilterCard,
        AudioSampleRateFilterCard,
        PeopleFilterCard,
        LocationsFilterCard
    ]);
    const hasAnyFilters = selectedFilters.length > 0;
    const trigger = useMemo(()=>/*#__PURE__*/ _jsx(Button, {
            appearance: "filled",
            color: "grey",
            "data-testid": ADD_FILTER_BUTTON,
            prefix: /*#__PURE__*/ _jsx(Plus, {
                className: "size-4"
            }),
            size: "medium",
            children: "Add filter"
        }), []);
    const showFiltersContent = hasAnyFilters || !isAboveMediumScreen;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FiltersDropdownHeader, {
                title: title
            }),
            showFiltersContent && /*#__PURE__*/ _jsxs("div", {
                className: classNames("flex flex-col overflow-auto scrollbar-thin scrollbar-track-grey-2 scrollbar-thumb-grey-7", searchImprovements ? "gap-0 bg-grey-2 px-4 py-3 md:flex-1 md:bg-grey-1 md:p-0" : "flex-1 gap-2 bg-grey-3 p-2"),
                ref: filtersContainerRef,
                children: [
                    !isAboveMediumScreen && !selectedFilters.length && /*#__PURE__*/ _jsx("div", {
                        className: "flex h-52 flex-col items-center justify-center rounded-8 border border-grey-3 bg-grey-1",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center gap-4 text-center",
                            children: [
                                /*#__PURE__*/ _jsx(Filters, {
                                    className: "size-[72px] text-grey-7"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-grey-11",
                                    children: "You have no filters applied."
                                })
                            ]
                        })
                    }),
                    selectedFilters.map((filter)=>renderFilterCard(filter))
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: classNames("flex justify-between py-3", showFiltersContent && !searchImprovements ? "border-t border-t-grey-5" : "", searchImprovements ? "sticky bottom-0 bg-grey-2 px-4 md:-mt-px md:border-t md:border-t-grey-5 md:bg-grey-1" : "px-3"),
                children: [
                    /*#__PURE__*/ _jsx(FiltersMenu, {
                        options: addButtonFilters,
                        trigger: trigger
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            (!!secondaryCTA || !!selectedFilters.length || !searchImprovements) && /*#__PURE__*/ _jsx(Button, {
                                appearance: searchImprovements ? "outline" : "ghost",
                                color: "grey",
                                "data-testid": CLEAR_ALL_FILTERS_BUTTON,
                                disabled: !selectedFilters.length,
                                size: "medium",
                                onClick: clearSelectedFilters,
                                children: searchImprovements ? "Clear" : "Clear all"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "filled",
                                color: "blue",
                                "data-testid": APPLY_FILTERS_BUTTON,
                                disabled: !isFilterChanged,
                                size: "medium",
                                onClick: applySelectedFilters,
                                children: "Apply"
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
FiltersDropdown.displayName = "FiltersDropdown";
